import { useTranslation } from "react-i18next"
import { CheckCircle, DownloadIcon } from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

import { useDownloadCsvErrors } from "../download-errors"
import { PreviewTable } from "../preview-table"
import type { ImportProgress, MergedRow } from "../schemas"

type Props = {
  isCompleted: boolean
  onClose: () => void
  onReset: () => void
  progress: ImportProgress
  rows: MergedRow[]
}

export function StepImporting({
  isCompleted,
  onClose,
  onReset,
  progress,
  rows,
}: Props) {
  const { t } = useTranslation()
  const { downloadErrorCsv } = useDownloadCsvErrors(rows)
  const percentage = progress.total
    ? Math.round((progress.completed / progress.total) * 100)
    : 0

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span>
            {isCompleted
              ? t("noCallList.importCSV.importing.complete")
              : t("noCallList.importCSV.importing.progress", {
                  completed: progress.completed,
                  total: progress.total,
                })}
          </span>
          <span>{percentage}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
          <div
            className="
              h-full rounded-full bg-primary-300 transition-all duration-300
            "
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {isCompleted && (
        <div className="flex gap-3">
          <Alert>
            <CheckCircle className="size-4" />
            <AlertDescription>
              {t("noCallList.importCSV.importing.summary", {
                errors: progress.errors,
                success: progress.completed - progress.errors,
              })}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <PreviewTable rows={rows} />

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {isCompleted && progress.errors > 0 && (
            <Button variant="outline" onClick={downloadErrorCsv}>
              <DownloadIcon className="mr-1 size-4" />
              {t("noCallList.importCSV.preview.downloadErrors")}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {isCompleted && (
            <Button variant="outline" onClick={onReset}>
              {t("noCallList.importCSV.importing.importAnother")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={!isCompleted}>
            {t("global.button.close")}
          </Button>
        </div>
      </div>
    </div>
  )
}
